import { SearchField } from '@components/search';
import { Item } from '@components/select';
import { TextField } from '@components/textField';
import { DateTimePicker } from '@components/timePicker/DateTimePicker';
import { Chip } from '@components/chip';
import { Checkbox } from '@components/checkbox';
import { Autocomplete, Box, FormControlLabel, Grid, Typography, RadioGroup, SxProps } from '@mui/material';
import { TableFilter, TFilterValues, TTableFilter } from '@components/table/types';
import { isDateValid } from '@utils/date';
import { DatePicker } from '@components/datePicker';
import { DateRangePicker } from '@components/datePicker/DateRangePicker';
import { endOfDay, startOfDay } from 'date-fns';
import { CoverageCategoryTypeaheadFilter } from '@components/table/filters/CoverageCategoryTypeaheadFilter';
import { InsuranceLineTypeaheadFilter } from '@components/table/filters/InsuranceLineTypeaheadFilter';
import { LineOfBusinessTypeaheadFilter } from '@components/table/filters/LineOfBusinessTypeaheadFilter';
import { OfferingTypeaheadFilter } from '@components/table/filters/OfferingTypeaheadFilter';
import { BusinessClassTypeaheadFilter } from '@components/table/filters/BusinessClassTypeaheadFilter';
import { ProductLabelTypeaheadFilter } from '@components/table/filters/ProductLabelTypeaheadFilter';
import { WritingCompanyTypeaheadFilter } from '@components/table/filters/WritingCompanyTypeaheadFilter';
import { UnderwriterTypeaheadFilter } from '@components/table/filters/UnderwriterTypeaheadFilter';
import { BrokerageTypeaheadFilter } from '@components/table/filters/BrokerageTypeaheadFilter';
import { BrokerageProducerTypeaheadFilter } from '@components/table/filters/BrokerageProducerTypeaheadFilter';
import { DateMinMax, ValueLabelPair } from '@components/table/types/DateMinMax.type';
import { JurisdictionTypeaheadFilter } from '@components/table/filters/JurisdictionTypeaheadFilter';
import { BrokerageDivisionTypeaheadFilter } from '@components/table/filters/BrokerageDivisionTypeaheadFilter';
import { QuoteTypeTypeaheadFilter } from '@components/table/filters/QuoteTypeTypeaheadFilter';
import { useDebouncedCallback } from 'use-debounce';
import { DEBOUNCE_DELAYS } from '@constants/debounceDelays';
import { useState } from 'react';
import { NumberRangeField } from '@components/forms/fields/NumberRangeField';
import { QuoteStatusTypeaheadFilter } from '@components/table/filters/QuoteStatusTypeaheadFilter';
import { PolicyStatusFilter } from '@components/table/filters/PolicyStatusFilter';
import { QuoteOriginTypeaheadFilter } from '@components/table/filters/QuoteOriginTypeaheadFilter';
import { ReportingQuoteSourceTypeaheadFilter } from '@components/table/filters/ReportingQuoteSourceTypeaheadFilter';
import { UnderwritingActivityTypeTypeaheadFilter } from '@components/table/filters/UnderwritingActivityTypeTypeaheadFilter';
import { PostRateDefaultFactorTypeTypeaheadFilter } from '@components/table/filters/PostRateDefaultFactorTypeTypeaheadFilter';

export type TableFiltersProps = {
  filters?: TTableFilter[];
  onFilterChange: (key: string, value: any) => void;
  selectedValues?: { [key: string]: any };
  defaultFilters?: TFilterValues;
  sxProps?: {
    container?: SxProps;
  };
  isMobileScreen?: boolean;
};

export const FilterComponent = ({
  selectedValues,
  componentProps,
  type,
}: {
  selectedValues?: { [key: string]: any };
  componentProps: {
    id: string;
    label?: string;
    disabled?: boolean;
    onChange: (value: any) => void;
    watchedField?: string;
    childrenFields?: string[];
    [x: string]: any;
  };
  type: TableFilter;
}) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { other, defaultValue, value, watchedField, fieldConfig, childrenFields, helperText, ...rest } = componentProps;
  const [preDebouncedFormValue, setPreDebouncedFormValue] = useState(value ?? defaultValue);
  const debouncedChangedValue = useDebouncedCallback((value) => {
    componentProps.onChange(value);
  }, DEBOUNCE_DELAYS.TYPING);

  switch (type) {
    case TableFilter.SEARCH:
      return (
        <SearchField
          sx={{
            '& legend span': { display: 'none' },
          }}
          variant='standard'
          {...rest}
          overrideValue={value}
          onChange={(value) => {
            componentProps.onChange(value);
          }}
          data-testid={`search-field-${componentProps.id}`}
        />
      );
    case TableFilter.TEXT_FIELD:
      return (
        <TextField
          {...rest}
          defaultValue={defaultValue}
          value={preDebouncedFormValue}
          fullWidth
          onChange={(e: any) => {
            const value = e.target.value;
            setPreDebouncedFormValue(value);
            debouncedChangedValue(value);
          }}
          key={`textbox-field-${componentProps.id}-${value ?? '0'}`}
          data-testid={`textbox-field-${componentProps.id}`}
        />
      );
    case TableFilter.SELECT_SINGLE:
      return (
        <Autocomplete
          {...rest}
          // value becoming 'undefined/null/empty "' doesn't cause selected label to clear out without the key changing...
          key={`filterOptions-${componentProps.id}-${value ?? '0'}`}
          // ^^ forces it to
          onChange={(_, value: any) => {
            componentProps.onChange(value);
          }}
          getOptionLabel={(option: Item | string) => {
            if (typeof option === 'string') {
              const selectedValue = other?.options?.find((x: Item) => x.value === option);
              return selectedValue?.label ?? option;
            }
            return option.label;
          }}
          renderInput={(params: any) => (
            <TextField {...params} inputLabel={componentProps.label ?? ''} helperText={helperText} />
          )}
          renderOption={(props, option: any) =>
            (option as Item)?.value === '' || (option as Item)?.hidden ? null : (
              <li {...props} key={option.id}>
                <Typography variant='bodySmall' color='text.main'>
                  {(option as Item).label}
                </Typography>
              </li>
            )
          }
          value={value ?? defaultValue}
          onInputChange={componentProps?.other.onInputChange}
          slotProps={{
            listbox: {
              ...componentProps?.other.slotProps?.listbox,
            },
            ...componentProps?.other.slotProps,
          }}
          options={componentProps?.other?.options ?? []}
          fullWidth
          isOptionEqualToValue={(option, value) => {
            return option === (value as unknown as string);
          }}
        />
      );

    case TableFilter.DATE_TIME:
      return (
        <DateTimePicker
          {...rest}
          onChange={(value: Date | null) => {
            if (isDateValid(value)) {
              componentProps.onChange(value);
            } else {
              componentProps.onChange(null);
            }
          }}
          defaultDate={other?.defaultDate}
        />
      );
    case TableFilter.DATE:
      return (
        <DatePicker
          {...rest}
          label=''
          textFieldProps={{
            inputLabel: rest?.label,
          }}
          onChange={(value: Date | null) => {
            if (isDateValid(value)) {
              componentProps.onChange(value?.toDateString());
            } else {
              componentProps.onChange(null);
            }
          }}
          defaultValue={defaultValue ? new Date(defaultValue) : undefined}
          maxDate={new Date()}
        />
      );
    case TableFilter.DATE_RANGE: {
      const dateRangeValue = value as any;

      return (
        <DateRangePicker
          value={dateRangeValue}
          defaultValue={defaultValue}
          disabled={componentProps.disabled}
          label={componentProps.label}
          onMinChange={(value: Date | null, maxDate: Date | null) => {
            if (isDateValid(value)) {
              componentProps.onChange({
                type: TableFilter.DATE_RANGE,
                min: startOfDay(value as Date),
                max: maxDate ? (isDateValid(maxDate) ? maxDate : null) : null,
              });
            } else {
              componentProps.onChange({ type: TableFilter.DATE_RANGE, min: null, max: maxDate });
            }
          }}
          onMaxChange={(value: Date | null, minDate: Date | null) => {
            if (isDateValid(value)) {
              componentProps.onChange({
                type: TableFilter.DATE_RANGE,
                max: endOfDay(value as Date),
                min: minDate ? (isDateValid(minDate) ? minDate : null) : null,
              });
            } else {
              componentProps.onChange({ type: TableFilter.DATE_RANGE, max: null, min: minDate });
            }
          }}
          displayMode={fieldConfig?.dateRangeField?.displayMode}
          {...other}
        />
      );
    }
    case TableFilter.NUMBER_RANGE: {
      const minNumberValue = value as any;

      return (
        <NumberRangeField
          value={minNumberValue}
          defaultValue={defaultValue}
          disabled={componentProps.disabled}
          label={componentProps.label}
          onMinChange={(value: number | null, maxNumber: number | null) => {
            componentProps.onChange({
              type: TableFilter.NUMBER_RANGE,
              min: value,
              max: maxNumber ?? null,
            });
          }}
          onMaxChange={(value: number | null, minNumber: number | null) => {
            componentProps.onChange({
              type: TableFilter.NUMBER_RANGE,
              max: value,
              min: minNumber ?? null,
            });
          }}
          {...other}
        />
      );
    }
    case TableFilter.CHIP_GROUP: {
      const options = other.options ?? [];
      const value = componentProps.value;

      return (
        <RadioGroup sx={{ height: '100%' }} defaultValue={defaultValue} name='chip-group'>
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              ...other?.sxProps,
            }}
          >
            {options.map((opt: { value: string; label: string; sx?: any }) => (
              <Chip
                key={opt.value}
                label={opt?.label}
                onClick={() => {
                  componentProps.onChange(opt.value);
                }}
                variant={value === opt.value ? 'filled' : 'outlined'}
              />
            ))}
          </Box>
        </RadioGroup>
      );
    }
    case TableFilter.CHECKBOX: {
      const checkbox = (
        <Checkbox
          sxProps={{ height: 5, background: 'transparent' }}
          checked={componentProps.value?.value == 'true' ? true : false}
          onChange={() => {
            componentProps.onChange({
              type: TableFilter.CHECKBOX,
              value: componentProps.value?.value == 'true' ? 'false' : 'true',
            });
          }}
        />
      );
      // Render the label beside the box so the control reads on its own (the inline filter bar has no
      // separate heading for it). The advanced-filters modal supplies its own heading, so it skips
      // this label to avoid duplicating it.
      return componentProps.label ? (
        <FormControlLabel
          control={checkbox}
          label={componentProps.label}
          slotProps={{ typography: { variant: 'bodySmall' } }}
          sx={{ ml: 0, gap: 0.5 }}
        />
      ) : (
        checkbox
      );
    }
    // common reusable type ahead filter definitions that load their own lists
    case TableFilter.OFFERING_TYPEAHEAD:
      return <OfferingTypeaheadFilter {...componentProps} selectedValues={selectedValues} />;
    case TableFilter.BUSINESS_CLASS_TYPEAHEAD:
      return <BusinessClassTypeaheadFilter {...componentProps} selectedValues={selectedValues} />;
    case TableFilter.PRODUCT_LABEL_TYPEAHEAD:
      return <ProductLabelTypeaheadFilter {...componentProps} selectedValues={selectedValues} />;
    case TableFilter.WRITING_COMPANY_TYPEAHEAD:
      return <WritingCompanyTypeaheadFilter {...componentProps} selectedValues={selectedValues} />;
    case TableFilter.UNDERWRITER_TYPEAHEAD_FILTER:
      return <UnderwriterTypeaheadFilter {...componentProps} selectedValues={selectedValues} />;
    case TableFilter.BROKERAGE_TYPEAHEAD:
      return <BrokerageTypeaheadFilter {...componentProps} selectedValues={selectedValues} />;
    case TableFilter.BROKERAGE_PRODUCER_TYPEAHEAD:
      return <BrokerageProducerTypeaheadFilter {...componentProps} selectedValues={selectedValues} />;
    case TableFilter.JURISDICTION_TYPEAHEAD:
      return <JurisdictionTypeaheadFilter {...componentProps} selectedValues={selectedValues} />;
    case TableFilter.BROKERAGE_DIVISION_TYPEAHEAD:
      return <BrokerageDivisionTypeaheadFilter {...componentProps} selectedValues={selectedValues} />;
    case TableFilter.TRANSACTION_TYPE_TYPEAHEAD:
      return <QuoteTypeTypeaheadFilter {...componentProps} selectedValues={selectedValues} />;
    case TableFilter.QUOTE_STATUS_TYPEAHEAD:
      return <QuoteStatusTypeaheadFilter {...componentProps} selectedValues={selectedValues} />;
    case TableFilter.POLICY_STATUS_FILTER:
      return <PolicyStatusFilter {...componentProps} selectedValues={selectedValues} />;
    case TableFilter.QUOTE_ORIGIN_TYPEAHEAD:
      return <QuoteOriginTypeaheadFilter {...componentProps} selectedValues={selectedValues} />;
    case TableFilter.REPORTING_QUOTE_SOURCE_TYPEAHEAD:
      return <ReportingQuoteSourceTypeaheadFilter {...componentProps} selectedValues={selectedValues} />;
    case TableFilter.UNDERWRITING_ACTIVITY_TYPE_TYPEAHEAD:
      return <UnderwritingActivityTypeTypeaheadFilter {...componentProps} selectedValues={selectedValues} />;
    case TableFilter.POST_RATE_DEFAULT_FACTOR_TYPEAHEAD:
      return <PostRateDefaultFactorTypeTypeaheadFilter {...componentProps} selectedValues={selectedValues} />;
    case TableFilter.INSURANCE_LINE_TYPEAHEAD:
      return <InsuranceLineTypeaheadFilter {...componentProps} selectedValues={selectedValues} />;
    case TableFilter.LINE_OF_BUSINESS_TYPEAHEAD:
      return <LineOfBusinessTypeaheadFilter {...componentProps} selectedValues={selectedValues} />;
    case TableFilter.COVERAGE_CATEGORY_TYPEAHEAD:
      return <CoverageCategoryTypeaheadFilter {...componentProps} selectedValues={selectedValues} />;
    default:
      throw Error('Filter type not yet implemented');
  }
};

export const TableFilters = ({
  filters,
  onFilterChange,
  selectedValues,
  defaultFilters,
  sxProps,
  isMobileScreen,
}: TableFiltersProps) => {
  const filtersToShow = filters?.filter((x) =>
    isMobileScreen ? x.type === TableFilter.SEARCH || x?.other?.showOnAnyScreenSize : true,
  );

  if (!filtersToShow || filtersToShow.length === 0) {
    return null;
  }

  return (
    <Box mt={0} mb={3} sx={{ width: '100%', ...sxProps?.container }}>
      <Grid container alignItems='center' spacing={2} sx={{ height: '100%', width: '100%', alignItems: 'flex-start' }}>
        {filtersToShow.map((filter) => (
          <Grid
            key={`filter-${filter.field}`}
            size={filter.width.gridWidth ?? filter.width.gridWidth}
            sx={{
              width: filter?.width?.setWidth,
              height: filter?.height,
            }}
          >
            <FilterComponent
              selectedValues={selectedValues}
              componentProps={{
                name: filter.field,
                watchedField: filter.watchedField,
                childrenFields: filter.childrenFields,
                disabled: filter?.isDisabled?.(selectedValues),
                id: filter.field,
                helperText: filter.helperText,
                label: filter.label,
                value: selectedValues && selectedValues[filter.field],
                onChange: (value: string | number | DateMinMax | ValueLabelPair) => {
                  onFilterChange(filter.field, value);
                },
                defaultValue: defaultFilters && defaultFilters[filter.field],
                other: filter?.other,
                fieldConfig: filter?.fieldConfig,
              }}
              type={filter.type}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};
